// "Play on your phone" panel: a QR code of the game's address, the address as
// text, and a Copy link button. Self-contained: it builds its own DOM and only
// needs src/shared/share.css. Either page can mount one.

import { renderQrCode } from './qrcode.js';
import { isLocalOnly } from './shareUrl.js';

const COPIED_MS = 1500;

export function createSharePanel({
  url,
  title = 'Play on your phone',
  hint = 'Point your phone camera at the code.',
  mount = document.body,
} = {}) {
  const doc = mount.ownerDocument;
  const { hostname, port } = new URL(url);

  const veil = doc.createElement('div');
  veil.className = 'share-veil';
  veil.hidden = true;
  veil.innerHTML = `
    <div class="share-panel" role="dialog" aria-modal="true" aria-labelledby="share-title">
      <div class="share-head">
        <h2 id="share-title" class="share-title"></h2>
        <button type="button" class="share-close" aria-label="Close">&times;</button>
      </div>
      <div class="share-qr" role="img"></div>
      <p class="share-hint"></p>
      <p class="share-warn" hidden></p>
      <div class="share-link">
        <code class="share-url"></code>
        <button type="button" class="share-copy">Copy link</button>
      </div>
    </div>`;

  const panel = veil.querySelector('.share-panel');
  const qrEl = veil.querySelector('.share-qr');
  const closeBtn = veil.querySelector('.share-close');
  const copyBtn = veil.querySelector('.share-copy');
  const urlEl = veil.querySelector('.share-url');
  const warnEl = veil.querySelector('.share-warn');

  veil.querySelector('.share-title').textContent = title;
  veil.querySelector('.share-hint').textContent = hint;
  qrEl.setAttribute('aria-label', `QR code for ${url}`);
  urlEl.textContent = url;
  if (isLocalOnly(hostname)) {
    warnEl.hidden = false;
    warnEl.textContent = 'This address only works on this computer. Open the Network address that '
      + `npm run dev prints (like http://192.168.x.x:${port || '5173'}) here first, then scan.`;
  }
  mount.appendChild(veil);

  let rendered = false;
  let returnFocus = null;
  let copiedTimer = null;

  function onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') {
      // keep focus inside the panel while it's open
      const focusable = [closeBtn, copyBtn];
      const i = focusable.indexOf(doc.activeElement);
      const next = e.shiftKey ? (i <= 0 ? focusable.length - 1 : i - 1) : (i + 1) % focusable.length;
      e.preventDefault();
      focusable[next].focus();
    }
  }

  function open() {
    if (!rendered) { renderQrCode(qrEl, url); rendered = true; }
    returnFocus = doc.activeElement;
    veil.hidden = false;
    doc.addEventListener('keydown', onKeydown);
    closeBtn.focus();
  }

  function close() {
    if (veil.hidden) return;
    veil.hidden = true;
    doc.removeEventListener('keydown', onKeydown);
    if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
  }

  async function copy() {
    const clipboard = doc.defaultView?.navigator?.clipboard;
    try {
      if (!clipboard) throw new Error('no clipboard');
      await clipboard.writeText(url);
      copyBtn.textContent = 'Copied';
    } catch {
      // no clipboard access (insecure origin, permissions): select the text instead
      const range = doc.createRange();
      range.selectNodeContents(urlEl);
      const sel = doc.defaultView.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      copyBtn.textContent = 'Selected, press Copy';
    }
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => { copyBtn.textContent = 'Copy link'; }, COPIED_MS);
  }

  closeBtn.addEventListener('click', close);
  copyBtn.addEventListener('click', copy);
  veil.addEventListener('click', (e) => { if (e.target === veil) close(); });
  panel.addEventListener('click', (e) => e.stopPropagation());

  return { element: veil, open, close, isOpen: () => !veil.hidden };
}
